// Admin > Lookups page. Tabs for every admin-managed vocabulary: product_type,
// account_type, frt_category, the mig 0010 option lists, OOB types (0013),
// and the device / phone asset option lists (0015).

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { lookups } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useMe } from '@/hooks/useMe';

type Tab =
  | 'product-types'
  | 'account-types'
  | 'frt-categories'
  | 'bank-info-options'
  | 'citizenship-options'
  | 'tester-status-options'
  | 'payment-status-options'
  | 'oob-types'
  | 'device-ownership-options'
  | 'phone-provider-options'
  | 'phone-payment-plan-options'
  | 'platform-options'
  | 'phone-carrier-type-options'
  | 'phone-payment-responsibility-options'
  | 'phone-test-status-options'
  | 'phone-usage-status-options'
  | 'device-manufacturer-options'
  | 'device-model-options'
  | 'device-os-options'
  | 'device-sim-restriction-options'
  | 'device-root-status-options';

interface CodedRow {
  id: number;
  code: string;
  name: string;
  active: boolean;
}
interface NamedRow {
  id: number;
  name: string;
  active: boolean;
  is_customer: boolean;
}

export function LookupsPage() {
  const { data: me } = useMe();
  const [tab, setTab] = useState<Tab>('product-types');

  if (me && me.role === 'user') {
    return (
      <div className="text-sm text-slate-600">
        Lookups management requires admin or senior role.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-slate-900">Lookups</h1>
      <div className="flex flex-wrap gap-2 border-b border-slate-200">
        <TabButton active={tab === 'product-types'} onClick={() => setTab('product-types')}>
          Product Types
        </TabButton>
        <TabButton active={tab === 'account-types'} onClick={() => setTab('account-types')}>
          Account Types
        </TabButton>
        <TabButton active={tab === 'frt-categories'} onClick={() => setTab('frt-categories')}>
          FRT Categories
        </TabButton>
        <TabButton active={tab === 'bank-info-options'} onClick={() => setTab('bank-info-options')}>
          Bank Info
        </TabButton>
        <TabButton
          active={tab === 'citizenship-options'}
          onClick={() => setTab('citizenship-options')}
        >
          Citizenship
        </TabButton>
        <TabButton
          active={tab === 'tester-status-options'}
          onClick={() => setTab('tester-status-options')}
        >
          Tester Status
        </TabButton>
        <TabButton
          active={tab === 'payment-status-options'}
          onClick={() => setTab('payment-status-options')}
        >
          Payment Status
        </TabButton>
        <TabButton active={tab === 'oob-types'} onClick={() => setTab('oob-types')}>
          OOB Types
        </TabButton>
        <TabButton active={tab === 'platform-options'} onClick={() => setTab('platform-options')}>
          Platform Types
        </TabButton>
        <TabButton active={tab === 'phone-carrier-type-options'} onClick={() => setTab('phone-carrier-type-options')}>
          Carrier Types
        </TabButton>
        <TabButton active={tab === 'phone-payment-responsibility-options'} onClick={() => setTab('phone-payment-responsibility-options')}>
          Payment Responsibility
        </TabButton>
        <TabButton active={tab === 'phone-test-status-options'} onClick={() => setTab('phone-test-status-options')}>
          Test Status
        </TabButton>
        <TabButton active={tab === 'phone-usage-status-options'} onClick={() => setTab('phone-usage-status-options')}>
          Usage Status
        </TabButton>
        <TabButton active={tab === 'device-manufacturer-options'} onClick={() => setTab('device-manufacturer-options')}>
          Phone Manufacturers
        </TabButton>
        <TabButton active={tab === 'device-model-options'} onClick={() => setTab('device-model-options')}>
          Phone Models
        </TabButton>
        <TabButton active={tab === 'device-os-options'} onClick={() => setTab('device-os-options')}>
          Operating Systems
        </TabButton>
        <TabButton active={tab === 'device-sim-restriction-options'} onClick={() => setTab('device-sim-restriction-options')}>
          SIM Restriction
        </TabButton>
        <TabButton active={tab === 'device-root-status-options'} onClick={() => setTab('device-root-status-options')}>
          Jailbroken / Rooted
        </TabButton>
        <TabButton
          active={tab === 'device-ownership-options'}
          onClick={() => setTab('device-ownership-options')}
        >
          Device Ownership
        </TabButton>
        <TabButton
          active={tab === 'phone-provider-options'}
          onClick={() => setTab('phone-provider-options')}
        >
          Carriers
        </TabButton>
        <TabButton
          active={tab === 'phone-payment-plan-options'}
          onClick={() => setTab('phone-payment-plan-options')}
        >
          Payment Plans
        </TabButton>
      </div>
      {tab === 'product-types' && <CodedTable kind="product-types" />}
      {tab === 'account-types' && <CodedTable kind="account-types" />}
      {tab === 'frt-categories' && <NamedTable />}
      {tab === 'bank-info-options' && (
        <SortedNamedTable
          title="Bank Info"
          api={lookups.bankInfoOptions}
          queryKey={['lookups', 'bank-info-options']}
        />
      )}
      {tab === 'citizenship-options' && (
        <SortedNamedTable
          title="Citizenship"
          api={lookups.citizenshipOptions}
          queryKey={['lookups', 'citizenship-options']}
        />
      )}
      {tab === 'tester-status-options' && (
        <SortedNamedTable
          title="Tester Status"
          api={lookups.testerStatusOptions}
          queryKey={['lookups', 'tester-status-options']}
        />
      )}
      {tab === 'payment-status-options' && (
        <SortedNamedTable
          title="Payment Status"
          api={lookups.paymentStatusOptions}
          queryKey={['lookups', 'payment-status-options']}
        />
      )}
      {tab === 'oob-types' && <OobTypeTable />}
      {tab === 'platform-options' && (
        <SortedNamedTable
          title="Platform Types"
          api={lookups.platformOptions}
          queryKey={['lookups', 'platform-options']}
        />
      )}
      {tab === 'phone-carrier-type-options' && (
        <SortedNamedTable
          title="Carrier Types"
          api={lookups.phoneCarrierTypeOptions}
          queryKey={['lookups', 'phone-carrier-type-options']}
        />
      )}
      {tab === 'phone-payment-responsibility-options' && (
        <SortedNamedTable
          title="Payment Responsibility"
          api={lookups.phonePaymentResponsibilityOptions}
          queryKey={['lookups', 'phone-payment-responsibility-options']}
        />
      )}
      {tab === 'phone-test-status-options' && (
        <SortedNamedTable
          title="Test Status"
          api={lookups.phoneTestStatusOptions}
          queryKey={['lookups', 'phone-test-status-options']}
        />
      )}
      {tab === 'phone-usage-status-options' && (
        <SortedNamedTable
          title="Usage Status"
          api={lookups.phoneUsageStatusOptions}
          queryKey={['lookups', 'phone-usage-status-options']}
        />
      )}
      {tab === 'device-manufacturer-options' && (
        <SortedNamedTable
          title="Phone Manufacturers"
          api={lookups.deviceManufacturerOptions}
          queryKey={['lookups', 'device-manufacturer-options']}
        />
      )}
      {tab === 'device-model-options' && (
        <SortedNamedTable
          title="Phone Models"
          api={lookups.deviceModelOptions}
          queryKey={['lookups', 'device-model-options']}
        />
      )}
      {tab === 'device-os-options' && (
        <SortedNamedTable
          title="Operating Systems"
          api={lookups.deviceOsOptions}
          queryKey={['lookups', 'device-os-options']}
        />
      )}
      {tab === 'device-sim-restriction-options' && (
        <SortedNamedTable
          title="SIM Restriction"
          api={lookups.deviceSimRestrictionOptions}
          queryKey={['lookups', 'device-sim-restriction-options']}
        />
      )}
      {tab === 'device-root-status-options' && (
        <SortedNamedTable
          title="Jailbroken / Rooted"
          api={lookups.deviceRootStatusOptions}
          queryKey={['lookups', 'device-root-status-options']}
        />
      )}
      {tab === 'device-ownership-options' && (
        <SortedNamedTable
          title="Device Ownership"
          api={lookups.deviceOwnershipOptions}
          queryKey={['lookups', 'device-ownership-options']}
        />
      )}
      {tab === 'phone-provider-options' && (
        <SortedNamedTable
          title="Carriers"
          api={lookups.phoneProviderOptions}
          queryKey={['lookups', 'phone-provider-options']}
        />
      )}
      {tab === 'phone-payment-plan-options' && (
        <SortedNamedTable
          title="Phone Payment Plans"
          api={lookups.phonePaymentPlanOptions}
          queryKey={['lookups', 'phone-payment-plan-options']}
        />
      )}
    </div>
  );
}

// Type values with built-in behavior on the account form; anything else is a
// free-text field (≤ 50 chars) labelled with the Type.
const OOB_FIELD_TYPE_HINTS = ['Phone', 'Email', 'NA', 'Device Name', 'App Name'];

interface OobDraft {
  name: string;
  contact_value: boolean;
  field_type: string;
  sort_order: string;
}
const EMPTY_OOB_DRAFT: OobDraft = { name: '', contact_value: false, field_type: '', sort_order: '' };

function OobTypeTable() {
  const queryClient = useQueryClient();
  const queryKey = ['lookups', 'oob-types'] as const;
  const { data, isLoading, error } = useQuery({ queryKey, queryFn: () => lookups.oobTypes.list() });
  const invalidate = () => queryClient.invalidateQueries({ queryKey });
  const create = useMutation({
    mutationFn: (d: OobDraft) =>
      lookups.oobTypes.create({
        name: d.name.trim(),
        contact_value: d.contact_value,
        field_type: d.field_type.trim(),
        ...(d.sort_order ? { sort_order: Number(d.sort_order) } : {}),
      }),
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: number;
      patch: Partial<{ name: string; contact_value: boolean; field_type: string; active: boolean; sort_order: number }>;
    }) => lookups.oobTypes.update(id, patch),
    onSuccess: invalidate,
  });
  const [draft, setDraft] = useState<OobDraft>(EMPTY_OOB_DRAFT);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [edit, setEdit] = useState<OobDraft>(EMPTY_OOB_DRAFT);

  return (
    <Card>
      <CardHeader>
        <CardTitle>OOB Types</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <p className="text-xs text-slate-500">
          <b>Type</b> decides what the account form captures for each OOB entry:{' '}
          <b>Phone</b> = country code + number, <b>Email</b> = email address, <b>NA</b> = nothing,{' '}
          <b>Device Name</b> = the entry&apos;s Device Name; any other value (e.g. App Name) = a
          free-text field (50 chars) labelled with it.
        </p>
        <datalist id="oob-field-types">
          {OOB_FIELD_TYPE_HINTS.map((t) => (
            <option key={t} value={t} />
          ))}
        </datalist>
        <form
          className="flex flex-wrap gap-2 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (!draft.name.trim() || !draft.field_type.trim()) return;
            create.mutate(draft, { onSuccess: () => setDraft(EMPTY_OOB_DRAFT) });
          }}
        >
          <label className="block text-sm flex-1 min-w-40">
            <span className="block text-slate-600">OOB Type</span>
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </label>
          <label className="block text-sm">
            <span className="block text-slate-600">Contact Value</span>
            <select
              value={draft.contact_value ? 'Y' : 'N'}
              onChange={(e) => setDraft({ ...draft, contact_value: e.target.value === 'Y' })}
              className="block rounded border border-slate-300 bg-white px-2 py-2 text-sm"
            >
              <option value="Y">Y</option>
              <option value="N">N</option>
            </select>
          </label>
          <label className="block text-sm w-40">
            <span className="block text-slate-600">Type</span>
            <Input
              list="oob-field-types"
              value={draft.field_type}
              onChange={(e) => setDraft({ ...draft, field_type: e.target.value })}
            />
          </label>
          <label className="block text-sm w-24">
            <span className="block text-slate-600">Sort</span>
            <Input
              type="number"
              value={draft.sort_order}
              onChange={(e) => setDraft({ ...draft, sort_order: e.target.value })}
            />
          </label>
          <Button type="submit" disabled={create.isPending}>
            Add
          </Button>
        </form>
        {create.error && <div className="text-sm text-red-600">{(create.error as Error).message}</div>}
        {update.error && <div className="text-sm text-red-600">{(update.error as Error).message}</div>}
        {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
        {isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1 pr-3">ID</th>
                <th className="py-1 pr-3">OOB Type</th>
                <th className="py-1 pr-3">Contact Value</th>
                <th className="py-1 pr-3">Type</th>
                <th className="py-1 pr-3">Sort</th>
                <th className="py-1 pr-3">Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map((row) =>
                editingId === row.id ? (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="py-1 pr-3 text-slate-500">{row.id}</td>
                    <td className="py-1 pr-3">
                      <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
                    </td>
                    <td className="py-1 pr-3">
                      <select
                        value={edit.contact_value ? 'Y' : 'N'}
                        onChange={(e) => setEdit({ ...edit, contact_value: e.target.value === 'Y' })}
                        className="rounded border border-slate-300 bg-white px-2 py-2 text-sm"
                      >
                        <option value="Y">Y</option>
                        <option value="N">N</option>
                      </select>
                    </td>
                    <td className="py-1 pr-3">
                      <Input
                        list="oob-field-types"
                        value={edit.field_type}
                        onChange={(e) => setEdit({ ...edit, field_type: e.target.value })}
                      />
                    </td>
                    <td className="py-1 pr-3 w-24">
                      <Input
                        type="number"
                        value={edit.sort_order}
                        onChange={(e) => setEdit({ ...edit, sort_order: e.target.value })}
                      />
                    </td>
                    <td className="py-1 pr-3">{row.active ? 'Yes' : 'No'}</td>
                    <td className="py-1 text-right whitespace-nowrap space-x-2">
                      <Button
                        size="sm"
                        disabled={!edit.name.trim() || !edit.field_type.trim() || update.isPending}
                        onClick={() =>
                          update.mutate(
                            {
                              id: row.id,
                              patch: {
                                name: edit.name.trim(),
                                contact_value: edit.contact_value,
                                field_type: edit.field_type.trim(),
                                sort_order: Number(edit.sort_order) || 0,
                              },
                            },
                            { onSuccess: () => setEditingId(null) },
                          )
                        }
                      >
                        Save
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setEditingId(null)}>
                        Cancel
                      </Button>
                    </td>
                  </tr>
                ) : (
                  <tr key={row.id} className="border-t border-slate-100">
                    <td className="py-1 pr-3 text-slate-500">{row.id}</td>
                    <td className="py-1 pr-3">{row.name}</td>
                    <td className="py-1 pr-3">{row.contact_value ? 'Y' : 'N'}</td>
                    <td className="py-1 pr-3">{row.field_type}</td>
                    <td className="py-1 pr-3 text-slate-500 tabular-nums">{row.sort_order}</td>
                    <td className="py-1 pr-3">{row.active ? 'Yes' : 'No'}</td>
                    <td className="py-1 text-right whitespace-nowrap space-x-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => {
                          setEditingId(row.id);
                          setEdit({
                            name: row.name,
                            contact_value: row.contact_value,
                            field_type: row.field_type,
                            sort_order: String(row.sort_order),
                          });
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant={row.active ? 'destructive' : 'secondary'}
                        onClick={() => update.mutate({ id: row.id, patch: { active: !row.active } })}
                      >
                        {row.active ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function SortedNamedTable<T extends { id: number; name: string; active: boolean; sort_order: number }>({
  title,
  api,
  queryKey,
}: {
  title: string;
  api: {
    list: () => Promise<{ data: T[] }>;
    create: (body: { name: string; active?: boolean; sort_order?: number }) => Promise<T>;
    update: (
      id: number,
      body: { name?: string; active?: boolean; sort_order?: number },
    ) => Promise<T>;
  };
  queryKey: readonly string[];
}) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: queryKey as unknown as readonly unknown[],
    queryFn: () => api.list(),
  });
  const create = useMutation({
    mutationFn: (body: { name: string; sort_order?: number }) => api.create(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKey as unknown as readonly unknown[] }),
  });
  const update = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: number;
      patch: { name?: string; active?: boolean; sort_order?: number };
    }) => api.update(id, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKey as unknown as readonly unknown[] }),
  });
  const [name, setName] = useState('');
  const [sortOrder, setSortOrder] = useState('');
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <form
          className="flex gap-2 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            create.mutate(
              {
                name: name.trim(),
                ...(sortOrder ? { sort_order: Number(sortOrder) } : {}),
              },
              {
                onSuccess: () => {
                  setName('');
                  setSortOrder('');
                },
              },
            );
          }}
        >
          <label className="block text-sm flex-1">
            <span className="block text-slate-600">Name</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block text-sm w-28">
            <span className="block text-slate-600">Sort</span>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            />
          </label>
          <Button type="submit" disabled={create.isPending}>
            Add
          </Button>
        </form>
        {create.error && (
          <div className="text-sm text-red-600">{(create.error as Error).message}</div>
        )}
        {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
        {isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1 pr-3">ID</th>
                <th className="py-1 pr-3">Name</th>
                <th className="py-1 pr-3">Sort</th>
                <th className="py-1 pr-3">Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data?.data ?? []).map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="py-1 pr-3 text-slate-500">{row.id}</td>
                  <td className="py-1 pr-3">{row.name}</td>
                  <td className="py-1 pr-3 text-slate-500 tabular-nums">{row.sort_order}</td>
                  <td className="py-1 pr-3">{row.active ? 'Yes' : 'No'}</td>
                  <td className="py-1 text-right">
                    <Button
                      size="sm"
                      variant={row.active ? 'destructive' : 'secondary'}
                      onClick={() =>
                        update.mutate({ id: row.id, patch: { active: !row.active } })
                      }
                    >
                      {row.active ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        '-mb-px border-b-2 px-3 py-2 text-sm font-medium',
        active
          ? 'border-brand-600 text-brand-700'
          : 'border-transparent text-slate-600 hover:text-slate-900',
      )}
    >
      {children}
    </button>
  );
}

function CodedTable({ kind }: { kind: 'product-types' | 'account-types' }) {
  const queryClient = useQueryClient();
  const api = kind === 'product-types' ? lookups.productTypes : lookups.accountTypes;
  const queryKey = useMemo(() => ['lookups', kind] as const, [kind]);
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => api.list(),
  });

  const create = useMutation({
    mutationFn: (body: { code: string; name: string }) => api.create(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });
  const update = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: number;
      patch: { code?: string; name?: string; active?: boolean };
    }) => api.update(id, patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const [code, setCode] = useState('');
  const [name, setName] = useState('');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{kind === 'product-types' ? 'Product Types' : 'Account Types'}</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <form
          className="flex gap-2 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (!code.trim() || !name.trim()) return;
            create.mutate(
              { code: code.trim(), name: name.trim() },
              {
                onSuccess: () => {
                  setCode('');
                  setName('');
                },
              },
            );
          }}
        >
          <label className="block text-sm">
            <span className="block text-slate-600">Code</span>
            <Input value={code} onChange={(e) => setCode(e.target.value)} />
          </label>
          <label className="block text-sm flex-1">
            <span className="block text-slate-600">Name</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <Button type="submit" disabled={create.isPending}>
            Add
          </Button>
        </form>
        {create.error && <div className="text-sm text-red-600">{(create.error as Error).message}</div>}
        {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
        {isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1 pr-3">ID</th>
                <th className="py-1 pr-3">Code</th>
                <th className="py-1 pr-3">Name</th>
                <th className="py-1 pr-3">Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data?.data as CodedRow[] | undefined)?.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="py-1 pr-3 text-slate-500">{row.id}</td>
                  <td className="py-1 pr-3 font-mono">{row.code}</td>
                  <td className="py-1 pr-3">{row.name}</td>
                  <td className="py-1 pr-3">{row.active ? 'Yes' : 'No'}</td>
                  <td className="py-1 text-right">
                    <Button
                      size="sm"
                      variant={row.active ? 'destructive' : 'secondary'}
                      onClick={() => update.mutate({ id: row.id, patch: { active: !row.active } })}
                    >
                      {row.active ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}

function NamedTable() {
  const queryClient = useQueryClient();
  const queryKey = ['lookups', 'frt-categories'] as const;
  const { data, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => lookups.frtCategories.list(),
  });
  const create = useMutation({
    mutationFn: (body: { name: string; is_customer: boolean }) => lookups.frtCategories.create(body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });
  const update = useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: number;
      patch: { name?: string; active?: boolean; is_customer?: boolean };
    }) => lookups.frtCategories.update(id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
  });
  const [name, setName] = useState('');
  const [isCustomer, setIsCustomer] = useState(true);

  return (
    <Card>
      <CardHeader>
        <CardTitle>FRT Categories</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3">
        <form
          className="flex gap-2 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            create.mutate(
              { name: name.trim(), is_customer: isCustomer },
              { onSuccess: () => setName('') },
            );
          }}
        >
          <label className="block text-sm flex-1">
            <span className="block text-slate-600">Name</span>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="block text-sm">
            <span className="block text-slate-600">Counts as customer</span>
            <select
              value={isCustomer ? 'Y' : 'N'}
              onChange={(e) => setIsCustomer(e.target.value === 'Y')}
              className="block rounded border border-slate-300 bg-white px-2 py-2 text-sm"
            >
              <option value="Y">Y</option>
              <option value="N">N</option>
            </select>
          </label>
          <Button type="submit" disabled={create.isPending}>
            Add
          </Button>
        </form>
        <p className="text-xs text-slate-500">
          FIs in a category that doesn&apos;t count as a customer (e.g. Non-Customer (External FI))
          are offered only as the bank on linked accounts — not for FRT test accounts.
        </p>
        {create.error && <div className="text-sm text-red-600">{(create.error as Error).message}</div>}
        {update.error && <div className="text-sm text-red-600">{(update.error as Error).message}</div>}
        {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
        {isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-1 pr-3">ID</th>
                <th className="py-1 pr-3">Name</th>
                <th className="py-1 pr-3">Counts as customer</th>
                <th className="py-1 pr-3">Active</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(data?.data as NamedRow[] | undefined)?.map((row) => (
                <tr key={row.id} className="border-t border-slate-100">
                  <td className="py-1 pr-3 text-slate-500">{row.id}</td>
                  <td className="py-1 pr-3">{row.name}</td>
                  <td className="py-1 pr-3">
                    <select
                      value={row.is_customer ? 'Y' : 'N'}
                      onChange={(e) =>
                        update.mutate({ id: row.id, patch: { is_customer: e.target.value === 'Y' } })
                      }
                      className="rounded border border-slate-300 bg-white px-2 py-1 text-sm"
                    >
                      <option value="Y">Y</option>
                      <option value="N">N</option>
                    </select>
                  </td>
                  <td className="py-1 pr-3">{row.active ? 'Yes' : 'No'}</td>
                  <td className="py-1 text-right">
                    <Button
                      size="sm"
                      variant={row.active ? 'destructive' : 'secondary'}
                      onClick={() => update.mutate({ id: row.id, patch: { active: !row.active } })}
                    >
                      {row.active ? 'Deactivate' : 'Reactivate'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardBody>
    </Card>
  );
}
